import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'
import useSmartNavigate from 'hooks/useSmartNavigate'
import useSimpleGeneratorDataSource from 'hooks/useSimpleGeneratorDataSource'
import useDebug from 'hooks/useDebug'
import classnames from 'classnames'

import Block from 'components/fieldBlocks/Block'
import GeneratorBlock from 'components/fieldBlocks/GeneratorBlock'
import withUriParams from 'hocs/withUriParams'
import Cesium3dView from './Cesium3dView'

import {queryResultsData} from '../../data/eventQueries'

import {prepareResultsData} from '../../data/eventObjects'
import {breadcrumbsStore} from 'appBase/TopNav'
import dateUtils from 'utils/dateUtils'
import {CESIUM_3D_VIEW, FULL_SCREEN, SOLUTION} from 'constants/constants'


const SimpleSolutionView = compose(
    withUriParams([
        ['itemType', String]]),
    queryResultsData
)(props => {
    useDebug("SimpleSolutionView", props);

    const {itemType, field, scenario, solution, showSolution, showResults, additionalSections} = props;

    // if not exists use default value
    const loading = props.loading || {resultsData: false};

    const navigate = useSmartNavigate();
    const {t, i18n} = useTranslation();
    const _t = (value, obj) => t('appEvent.views.solutionViews.SimpleSolutionView.' + value, obj);

    // stable object to use as results dataSource
    const resultsData = useMemo(
        () => prepareResultsData(props.resultsData),
        [props.resultsData]);
    const resultsSource = useMemo(
        () => ({
            results: solution && solution.results,
            resultsData: (solution && solution.overrideResultsData) || resultsData || [],
            resultsData_loading: loading.resultsData
        }),
        [solution, resultsData, loading.resultsData]);
    const [resultsDataSource] = useSimpleGeneratorDataSource(resultsSource);

    // preparing links

    const containerId = SOLUTION;
    breadcrumbsStore.register(containerId);
    const breadcrumbs = [];
    breadcrumbs.push({
        name: (solution && solution.createdAt && dateUtils.dateToLocaleDateTimeString(solution.createdAt, i18n)) ||
        (scenario.type !== 'form' ? _t('solution'): _t('solutionForm')),
        to: `${props.uri}`});

    useEffect(() => {
        const isTerminal = !itemType;
        breadcrumbsStore.set(containerId, breadcrumbs, isTerminal);
    });

    // subnavigation

    if (![null, CESIUM_3D_VIEW].includes(itemType)) { // item not found
        navigate(`${props.uri}${props.location.search}`);
        return null;
    }

    // rendering

    const {disabled, progress, dataSource, onChange, onValidate} = props;

    const solutionStatus = solution && solution.status;

    const sections = [];
    // if we need sections
    if (showSolution) {
        sections.push({
            className: "solution-tab-icon",
            name: _t(scenario.type !== 'form' ? 'solution2': 'solution2Form'),
            title: _t(scenario.type !== 'form' ? 'solution2': 'solution2Form'),
            key: "solution"
        });
    }
    // if we need sections and results are available
    if (showResults && solutionStatus !== "ready") {
        sections.push({
            className: "solution-result-tab-icon",
            name: _t('results'),
            title: _t('results'),
            key: "solution-result"
        });
        if (scenario.show3dResults) {
            sections.push({
                key: "3d", name: _t('3dTrajectory'), className: "tab-icon sim-3D-tab-icon",
                onClick: () => navigate(`${props.uri}/${CESIUM_3D_VIEW}${props.location.search}`, {state: {[FULL_SCREEN]: true}})
            });
        }
    }
    if (additionalSections) {
        additionalSections.map( s =>
            sections.push({className: s.className, name: s.name, title: s.title, key: s.key}));
    }

    // render

    return (
        <>
        {!itemType && scenario.type !== 'form' &&
        <Block sections={sections}>
            {showSolution &&
            <Block section="solution" className={classnames(!sections && "workpanel-gray")}>
                <GeneratorBlock disabled={disabled}
                                data={dataSource}
                                field={field}
                                items={scenario.prototypeParameters || []}
                                collectionsDict={{
                                    parameters: {planet: scenario.planet}
                                }}
                                onChange={onChange}
                                onValidate={onValidate}/>
            </Block>}

            {additionalSections?.map(s =>
                <div key={s.key} section={s.key}>{s.component}</div>)}

            {showResults && solutionStatus !== "ready" &&
            <Block section="solution-result" className={classnames(!sections && "workpanel-gray")}>
                <p>{_t(scenario.type !== 'form' ? 'solutionNumber': 'solutionNumberForm')} {(solution && solution.number) || ""}:</p>
                {solutionStatus === "running" &&
                <p className="text-info">
                    {["test", "generative_test"].includes(scenario.type) ?
                        _t('runningScoring'):
                        _t('runningCalculation')}
                    {progress}
                </p>}
                {solutionStatus === "evaluating" &&
                <p className="text-info">{_t('evaluating')}</p>}
                {(solutionStatus === "evaluating" || solutionStatus === 'failed' || solutionStatus === 'finished') &&
                <GeneratorBlock data={resultsDataSource}
                                items={scenario.prototypeResults  || []} />}
            </Block>}
        </Block>}

        {!itemType && scenario.type === 'form' &&
            <div>
                <h6 className="mx-3">{_t('solution2Form')}</h6>
                <GeneratorBlock disabled={disabled}
                                data={dataSource}
                                field={field}
                                items={scenario.prototypeParameters || []}
                                collectionsDict={{
                                    parameters: {planet: scenario.planet}
                                }}
                                onChange={onChange}
                                onValidate={onValidate} />
            </div>}

        {itemType === CESIUM_3D_VIEW &&
        <Cesium3dView simulation={(solution && solution.simulation) || {}}
                      resultsData={resultsSource.resultsData}
                      skipSimulationDataCheck
                      loading={loading}
                      user={props.user}
                      uri={`${props.uri}/${itemType}`} uriParams={props.uriParams} location={props.location} />}
        </>);
});

export default SimpleSolutionView;